import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import sessionStorage from "./utils/sessionStore";
import { Form, Button, Alert, Spinner, Badge } from "react-bootstrap";
import { FaSearch } from "react-icons/fa";
import { ChevronRight } from "lucide-react";
import SearchableSelect from "./components/SearchableSelect";
import CollapsibleFilters from "./components/CollapsibleFilters";
import GridPagination from "./components/GridPagination";
import { authHeaders } from "./QuoteCreate";
import { isAdminOrDistrRole } from "./utils/auth";
import {
  customersDescriptor,
  warm as warmRefData,
} from "./utils/refDataCache";
import * as searchCache from "./utils/searchCache";
import { ORDERS_KEY, normalizeOrdersResponse } from "./utils/searchCache";
import {
  AUTOCOMPLETE_TERRITORY as AC_TERRITORY,
  buildSalesRepConfig,
  buildCustomerConfig,
} from "./utils/refDataConfigs";
import "./pagecss/Webappcss.css";
import "./pagecss/DynamicQw.css";
import "./pagecss/QuoteWizard.css";
import { sessionMap } from "./utils/sessionCaches";

const API_URL = process.env.REACT_APP_API_URL;

// -----------------------------------------------------------------------------
// Role-aware autocomplete configs — identical scoping rules to the Quote search
// page (QuoteCreate). Territory/Sales Rep/Customer dropdowns narrow one another
// client-side; the backend applies the caller's own territory/rep scope server-
// side (OrderRoleFilter) regardless of what's sent, so a rep only ever sees
// their own orders.
// -----------------------------------------------------------------------------
function getCurrentUserScope() {
  const role = (sessionStorage.getItem("userRole") || "").toLowerCase();
  const territoryId = sessionStorage.getItem("territoryId") || "";
  const isD99 = territoryId.toLowerCase() === "d99";
  return { role, territoryId, isUnrestricted: isAdminOrDistrRole(role) && isD99 };
}

const PAGE_SIZES = [10, 25, 50, 100];

// -----------------------------------------------------------------------------
// Build the GET /api/sales-orders querystring for ONE page request. Pure —
// shared by runSearch and the background next-page prefetch so both produce
// identical requests.
// -----------------------------------------------------------------------------
// Exported: CustomerDetail's Activity tab reuses this for its order download
// so both build byte-identical /api/sales-orders requests.
export function buildOrderSearchQs({ filters, page, pageSize, cursor, orderNumFind }) {
  const qs = new URLSearchParams();
  qs.set("page", String(page));
  qs.set("pageSize", String(pageSize));
  // Cursor paging (live mode). The cached path ignores these and uses page.
  if (cursor?.dir === "next")      qs.set("beforeOrderNum", String(cursor.orderNum));
  else if (cursor?.dir === "prev") qs.set("afterOrderNum", String(cursor.orderNum));
  const onum = orderNumFind.trim();
  if (/^\d+$/.test(onum)) qs.set("orderNum", onum);  // exact order-number match
  if (filters.custID)       qs.set("custId", filters.custID);
  if (filters.salesRepCode) qs.set("salesRepCode", filters.salesRepCode);
  if (filters.poNum.trim()) qs.set("poNum", filters.poNum.trim());
  // Status → the matching query param(s).
  if (filters.status === "open")        qs.set("status", "open");
  else if (filters.status === "closed") qs.set("status", "closed");
  else if (filters.status === "held")   qs.set("heldOnly", "true");
  // No startDate/endDate — the server applies the role-based default window.
  qs.set("sortBy", "OrderNum");
  qs.set("sortDir", "desc");
  return qs.toString();
}

// Cache-warm polling: 5s, 10s, 20s, 30s, 30s… then stop and offer a manual
// retry. 10 attempts ≈ 4.5 min of waiting, which comfortably covers a normal
// cold load; past that something is wrong and looping isn't going to fix it.
const WARM_BASE_DELAY_MS = 5000;
const WARM_MAX_DELAY_MS  = 30000;
const WARM_MAX_ATTEMPTS  = 10;

// Status filter — single-select. Each maps to the sales-order query params:
//   open   → status=open      (OpenOrder == true)
//   closed → status=closed    (OpenOrder == false)
//   held   → heldOnly=true    (OrderHeld == true)
// (The sales-orders API has no "voided" query param — voided orders aren't a
// separate filter here.)
const STATUS_OPTIONS = [
  { label: "Any status", value: "" },
  { label: "Open",       value: "open" },
  { label: "Held",       value: "held" },
  { label: "Closed",     value: "closed" },
];

// Light table header cell style — matches the Quote/territory/customer grids.
const QTH = { padding: "9px 12px", fontWeight: 700, whiteSpace: "nowrap" };
// Per-column filter cell (the search row under the header labels). Padding is
// trimmed from 6px to 3px and the inputs themselves are shortened via
// .dyn-colfilter — the row keeps working, it just stops costing ~55px.
const QTHF = { padding: "3px 8px", borderTop: "1px solid #E2E8F0", verticalAlign: "middle", background: "#F8FAFC" };

function fmtMoney(v) {
  if (v === null || v === undefined || Number.isNaN(Number(v))) return "—";
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(Number(v));
}
function fmtDate(v) {
  if (!v) return "—";
  try {
    const d = new Date(v);
    if (Number.isNaN(d.valueOf())) return "—";
    return d.toLocaleDateString();
  } catch { return "—"; }
}
// Compact M/D/YY — used by the phone card so the date doesn't eat the row.
function fmtDateShort(v) {
  if (!v) return "—";
  try {
    const d = new Date(v);
    if (Number.isNaN(d.valueOf())) return "—";
    return `${d.getMonth() + 1}/${d.getDate()}/${String(d.getFullYear()).slice(-2)}`;
  } catch { return "—"; }
}

// =============================================================================
// Order — single landing page for sales orders. Mirrors the Quote search page
// (Quote.js): a scoped, paginated search grid with a row-click → /order/:n
// (OrderView) navigation. Orders are created only by converting a quote, so
// there is no "+ Add" CTA here — instead a shortcut to the Quotes page.
// The /orders route is wired in index.js; the DB menu item (RoutePath=/orders)
// is added by SQL/024_AddOrdersMenuItem.sql.
// =============================================================================
function Order() {
  const navigate = useNavigate();
  const location = useLocation();

  // Pre-applied filters handed in via router state — e.g. the Customer page's
  // Activity tab navigates here with { initialFilters: { custID: ... } }.
  // Captured once in a ref so re-renders don't re-read stale router state.
  const initialFiltersRef = useRef(location.state?.initialFilters || null);
  const initialFilters = initialFiltersRef.current;

  // Auth gate — same pattern as Quote / QuoteCreate.
  useEffect(() => {
    if (!sessionStorage.getItem("authToken")) navigate("/login");
  }, [navigate]);

  // ── User scope (JWT-derived, used to scope the customer autocomplete) ──
  const userScope = useMemo(() => getCurrentUserScope(), []);

  // ── Filter state ────────────────────────────────────────────────────────
  // Filters start BLANK for everyone: the panel opens unfiltered and the
  // auto-search on mount shows the most recent orders within the user's scope.
  const [filters, setFilters] = useState(() => ({
    custID: "", salesRepCode: "", territoryID: "",
    status: "", poNum: "",
    // Router-state pre-fill wins over the blank defaults (Activity tab flow).
    ...(initialFilters || {}),
  }));

  // Warm this rep's customer list in the background so the Customer dropdown
  // opens instantly once expanded.
  useEffect(() => {
    if (filters.salesRepCode) warmRefData(customersDescriptor(filters.salesRepCode));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Configs rebuild when the narrowing fields change so the dropdowns reflect
  // the active scope. Territory narrows the rep+customer dropdowns client-side;
  // it is NOT sent to the API (the sales-orders endpoint has no territory param —
  // scoping is enforced server-side from the JWT).
  const salesRepConfig = useMemo(
    () => buildSalesRepConfig(filters.territoryID),
    [filters.territoryID]
  );
  const customerConfig = useMemo(
    () => buildCustomerConfig(filters.salesRepCode, filters.territoryID, userScope),
    [filters.salesRepCode, filters.territoryID, userScope]
  );

  // Pagination state — separate from filters so changing pages doesn't lose
  // input and changing filters resets us back to page 1.
  const [page, setPage]         = useState(1);
  const [pageSize, setPageSize] = useState(25);

  // ── Result state ──────────────────────────────────────────────────────────
  // Seed from the login-time prefetch when it's there (the default search is
  // cached at sign-in and after every default-filter search), so the grid
  // paints rows on first render; the mount effect then revalidates quietly.
  // With pre-applied filters the cached (unfiltered) default page would flash
  // wrong rows — start empty and let the mount search paint the filtered set.
  const [results, setResults]   = useState(
    () => (initialFilters
      ? { orders: [], totalCount: null, totalPages: 0 }
      : searchCache.peek(ORDERS_KEY) || { orders: [], totalCount: null, totalPages: 0 })
  );
  // Live mode pages by cursor, not page number: { dir: "next"|"prev", orderNum }.
  // null = first page. The server can't skip to page N — the Shukla filter runs
  // after Epicor answers — so we hand back a boundary OrderNum instead and every
  // page costs the same. MUST be cleared whenever the result set changes
  // (new search, filter change, page size), or we'd page from stale bounds.
  const [cursor, setCursor]     = useState(null);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState(null);
  // A cache-seeded grid already IS a search result — render count + pager
  // immediately instead of the "hit Search" empty state.
  const [hasSearched, setHasSearched] = useState(() => !initialFilters && searchCache.peek(ORDERS_KEY) !== undefined);
  // Cache-warming state. The API returns success + an empty list with
  // cacheInfo.warm === false while the sales-order cache is still doing its
  // cold load (a full 12-month Epicor pull that runs on app start). That is
  // "not loaded yet", NOT "no orders" — so we say so and poll until it lands.
  //
  // warmAttempts doubles as the retry counter and the effect's re-arm tick:
  // it increments on every warming response (so the timer re-arms even though
  // `warming` stays true) and resets to 0 the moment the cache goes warm.
  const [warming, setWarming]           = useState(false);
  const [warmAttempts, setWarmAttempts] = useState(0);
  // Order # — exact-match server filter (sent as orderNum on Search/Enter),
  // so it finds across all pages, not just the loaded rows.
  const [orderNumFind, setOrderNumFind] = useState("");
  // In-table (client-side) column search — narrows the ALREADY-LOADED page rows
  // in memory, like a data grid. It never calls the API (that's the panel's job).
  const [colFilters, setColFilters] = useState({ orderNum: "", customer: "", poNum: "", salesRep: "", orderDate: "", total: "", status: "" });
  const setCol = (k, v) => setColFilters((c) => ({ ...c, [k]: v }));

  // Expanded detail rows (order → shipments flow). A Set so several rows can
  // be open at once; survives paging so flipping back keeps them open.
  const [expandedOrders, setExpandedOrders] = useState(() => new Set());
  const toggleExpanded = (orderNum) => {
    setExpandedOrders((prev) => {
      const next = new Set(prev);
      if (next.has(orderNum)) next.delete(orderNum); else next.add(orderNum);
      return next;
    });
  };

  // Internal-scroll layout: filter panel collapses to a ribbon once the results
  // grid is scrolled, and a bottom fade signals more rows below.
  // Per-column client-side filter row. ON by default — it's a working part of
  // the grid, not decoration. The space it used to cost came from its own
  // padding instead (see .dyn-colfilter), and the toggle below lets anyone who
  // wants the extra rows put it away.
  const [showColFilters, setShowColFilters] = useState(true);
  // Start collapsed on every screen size (matches the Shipping page) — the
  // page auto-loads the newest orders anyway, so the grid gets the height.
  // Starts expanded when filters arrived via router state, so the user sees
  // which filters are pre-applied.
  const [filtersCollapsed, setFiltersCollapsed] = useState(() => !initialFilters);
  const resultsScrollRef = useRef(null);
  const [moreBelow, setMoreBelow] = useState(false);
  const refreshScrollCue = () => {
    const el = resultsScrollRef.current;
    if (!el) { setMoreBelow(false); return; }
    setMoreBelow(el.scrollHeight - el.scrollTop - el.clientHeight > 8);
  };
  const onResultsScroll = (e) => {
    if (e.currentTarget.scrollTop > 24 && !filtersCollapsed) setFiltersCollapsed(true);
    refreshScrollCue();
  };
  useEffect(() => {
    const id = window.setTimeout(refreshScrollCue, 0);
    return () => window.clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [results, filtersCollapsed, loading]);

  // ───────────────────────────────────────────────────────────────────────────
  // runSearch — fires GET /api/sales-orders with the active filter set. Mirrors
  // Quote.runSearch; response envelope is { success, orders, totalCount,
  // totalPages, page, pageSize, ... }.
  // ───────────────────────────────────────────────────────────────────────────
  // Per-result-set page cache: pageNum → normalized results, or a Promise for
  // a background prefetch still in flight. Visited pages rebind instantly on
  // Prev/Next, and the page AFTER the one on screen is always prefetching.
  // Cleared whenever the result set changes (Search click, Clear, page-size
  // change) — NOT on paging.
  const pageCacheRef = useRef(new Map());
  const searchSeqRef = useRef(0);   // newest search wins (UI-QTE-017)
  const clearPageCache = () => { pageCacheRef.current = new Map(); };

  // Fire-and-forget fetch of one page into the page cache. A "cache still
  // warming" envelope is NOT cached — it's a placeholder, not data.
  const prefetchPage = useCallback((targetPage, cursorArg) => {
    const cache = pageCacheRef.current;
    if (cache.has(targetPage)) return; // loaded or already in flight
    const qs = buildOrderSearchQs({ filters, page: targetPage, pageSize, cursor: cursorArg, orderNumFind });
    const promise = fetch(`${API_URL}/api/sales-orders?${qs}`, { headers: authHeaders() })
      .then((r) => r.json().then((data) => {
        if (!r.ok || data.success === false) throw new Error("prefetch failed");
        const normalized = normalizeOrdersResponse(data);
        if (data?.cacheInfo?.live !== true && data?.cacheInfo?.warm === false && normalized.orders.length === 0)
          throw new Error("cache warming");
        // Only commit if this cache generation is still current (a new search
        // may have swapped the Map while we were in flight).
        if (pageCacheRef.current === cache) cache.set(targetPage, normalized);
        return normalized;
      }))
      .catch(() => { cache.delete(targetPage); return null; });
    cache.set(targetPage, promise);
  }, [filters, pageSize, orderNumFind]);

  // Warm the page after the one just shown, using its boundary cursor (live
  // mode) or the plain page number (cached mode with a known total).
  const prefetchNext = useCallback((current, fromPage) => {
    if (current.hasMore !== undefined) {
      if (current.hasMore === true && current.lastOrderNum != null)
        prefetchPage(fromPage + 1, { dir: "next", orderNum: current.lastOrderNum });
    } else if (fromPage + 1 <= (current.totalPages || 0)) {
      prefetchPage(fromPage + 1, null);
    }
  }, [prefetchPage]);

  // `silent` = background revalidate over a cache-seeded grid (mount with a
  // cache hit, or the warm-poll): no spinner, cached rows stay put until real
  // data lands.
  // Newest search wins (UI-QTE-017, same as Quotes): a run that is no longer the
  // newest when its answer lands changes nothing — grid, page cache or prefetch.
  const runSearch = useCallback(async ({ silent = false } = {}) => {
    const seq = ++searchSeqRef.current;
    const isCurrent = () => seq === searchSeqRef.current;
    setError(null);

    // The mount query — blank filters, page 1 of 25, newest first — is the one
    // the session cache holds; every fresh copy of it is written back so a
    // return visit binds instantly.
    const isDefaultSearch =
      page === 1 && pageSize === 25 && !cursor && !orderNumFind.trim() &&
      Object.values(filters).every((v) => !v);

    // Page cache first: a visited page binds instantly; an in-flight prefetch
    // is awaited (spinner for its remaining time) instead of duplicated.
    let entry = pageCacheRef.current.get(page);
    if (entry) {
      if (typeof entry.then === "function") {
        if (!silent) setLoading(true);
        entry = await entry; // normalized results, or null if the prefetch failed
        if (!isCurrent()) return;
      }
      if (entry) {
        setResults(entry);
        setLoading(false);
        setHasSearched(true);
        setWarming(false);
        setWarmAttempts(0);
        if (isDefaultSearch) searchCache.put(ORDERS_KEY, entry);
        prefetchNext(entry, page);
        return;
      }
    }

    if (!silent) setLoading(true);
    const qs = buildOrderSearchQs({ filters, page, pageSize, cursor, orderNumFind });

    try {
      const r = await fetch(`${API_URL}/api/sales-orders?${qs}`, { headers: authHeaders() });
      const data = await r.json().catch(() => ({}));
      if (!isCurrent()) return;
      if (!r.ok || data.success === false) {
        setError(data.error || data.message || `HTTP ${r.status}`);
        setResults({ orders: [], totalCount: 0, totalPages: 0 });
        setWarming(false);
        setWarmAttempts(0);
      } else {
        const normalized = normalizeOrdersResponse(data);
        // Three guards, because a false positive here spins the Search button
        // forever on a grid that already has rows:
        //   • live responses never wait on the cache at all
        //   • rows on screen mean we are not waiting for anything
        const stillWarming =
          data?.cacheInfo?.live !== true &&
          data?.cacheInfo?.warm === false &&
          normalized.orders.length === 0;
        if (silent && stillWarming) {
          // Revalidating a cache-seeded grid and the server cache is mid-warm:
          // the empty "warming" envelope must NOT wipe the rows on screen.
          // Keep them, keep polling quietly until the warm data lands.
          setWarming(true);
          setWarmAttempts((n) => n + 1);
        } else {
          setResults(normalized);
          setWarming(stillWarming);
          setWarmAttempts((n) => (stillWarming ? n + 1 : 0));
          if (!stillWarming) {
            pageCacheRef.current.set(page, normalized);
            if (isDefaultSearch) searchCache.put(ORDERS_KEY, normalized);
            prefetchNext(normalized, page);
          }
        }
      }
    } catch (e) {
      if (!isCurrent()) return;
      setError(e.message);
      setResults({ orders: [], totalCount: 0, totalPages: 0 });
      setWarming(false);
      setWarmAttempts(0);
    } finally {
      if (isCurrent()) {
        setLoading(false);
        setHasSearched(true);
      }
    }
  }, [filters, page, pageSize, orderNumFind, cursor, prefetchNext]);

  // Auto-load the first page on mount: newest orders within the user's scope.
  // When the grid was seeded from the session cache this runs as a silent
  // revalidate — the cached rows stay on screen and refresh in place.
  useEffect(() => {
    runSearch({ silent: !initialFilters && searchCache.peek(ORDERS_KEY) !== undefined });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Re-run on page/pageSize change once the first load has happened. Skips the
  // mount render — hasSearched can start true off the cache seed, and the
  // mount effect above already searches.
  const pagingMounted = useRef(false);
  useEffect(() => {
    if (!pagingMounted.current) { pagingMounted.current = true; return; }
    if (hasSearched) runSearch();
  }, [page, pageSize]);  // eslint-disable-line react-hooks/exhaustive-deps

  // Poll while the server-side cache warms. Held in a ref so re-arming the
  // timer doesn't depend on runSearch's identity (which changes whenever a
  // filter changes and would otherwise restart the countdown mid-wait).
  //
  // Backoff, not a flat interval: a cold load is a multi-minute Epicor pull,
  // so polling it every 5s just floods the API with requests that can only
  // answer "still loading". Doubles 5s → 30s, then gives up and hands the
  // user a manual Retry rather than looping forever.
  const runSearchRef = useRef(runSearch);
  runSearchRef.current = runSearch;
  useEffect(() => {
    if (!warming || warmAttempts >= WARM_MAX_ATTEMPTS) return undefined;
    const delay = Math.min(WARM_BASE_DELAY_MS * 2 ** (warmAttempts - 1), WARM_MAX_DELAY_MS);
    // Silent: the poll refreshes in place — no spinner flash between attempts,
    // and cache-seeded rows (if any) stay on screen while the server warms.
    const id = window.setTimeout(() => runSearchRef.current({ silent: true }), delay);
    return () => window.clearTimeout(id);
  }, [warming, warmAttempts]);

  // Warming, but we've stopped polling — user has to ask for another try.
  const warmGaveUp = warming && warmAttempts >= WARM_MAX_ATTEMPTS;
  const retryWarm = () => { setWarmAttempts(0); runSearch(); };

  // Reset to page 1 whenever the user explicitly searches.
  // One-line recap of what's applied, shown on the collapsed ribbon.
  const activeFilterSummary = (() => {
    const parts = [];
    if (orderNumFind.trim()) parts.push(`Order #${orderNumFind.trim()}`);
    if (filters.territoryID) parts.push(filters.territoryID);
    if (filters.salesRepCode) parts.push(filters.salesRepCode);
    if (filters.custID) parts.push(filters.custID);
    if (filters.poNum) parts.push(`PO ${filters.poNum}`);
    const st = STATUS_OPTIONS.find((o) => o.value === filters.status);
    if (filters.status && st) parts.push(st.label);
    return parts.length ? parts.join(" · ") : "";
  })();

  const handleSearchClick = () => {
    // Collapse the panel on EVERY search, not just on phones. Once you've hit
    // Search your attention is on the results, and leaving the panel expanded
    // was costing ~200px — the difference between two visible rows and eight.
    // It's one click on "Filters" to bring it back, and the ribbon keeps the
    // active filters visible while collapsed.
    setFiltersCollapsed(true);
    // A new search invalidates the cursor — its OrderNum bounds belong to the
    // previous result set and would page into the wrong place — and the page
    // cache with it (Search always means "fetch fresh").
    clearPageCache();
    setCursor(null);
    if (page !== 1) setPage(1); else runSearch();
  };

  // Clear reloads the unfiltered first page (UI-ORD-017): on page 1 setPage(1) changes
  // nothing and no search ran; the tick re-runs it once the emptied filters are in state.
  const [clearTick, setClearTick] = useState(0);
  useEffect(() => {
    if (clearTick > 0) runSearch();
  }, [clearTick]);  // eslint-disable-line react-hooks/exhaustive-deps

  const handleClearFilters = () => {
    setFilters({ custID: "", salesRepCode: "", territoryID: "", status: "", poNum: "" });
    setOrderNumFind("");
    setColFilters({ orderNum: "", customer: "", poNum: "", salesRep: "", orderDate: "", total: "", status: "" });
    clearPageCache();
    setCursor(null);
    if (page !== 1) setPage(1); else setClearTick((n) => n + 1);
  };

  // Paging. In live mode there are no page numbers to jump to — only the row
  // above or below the current page is reachable — so a move becomes a cursor
  // plus a direction, and `page` survives purely as a display counter.
  const goToPage = (targetZeroBased) => {
    const target = targetZeroBased + 1;
    if (target === page) return;

    const liveMode = results.hasMore !== undefined;
    if (!liveMode) { setPage(target); return; }

    if (target > page && results.lastOrderNum != null) {
      setCursor({ dir: "next", orderNum: results.lastOrderNum });
      setPage(page + 1);          // always ±1: intermediate pages are unreachable
    } else if (target < page && results.firstOrderNum != null) {
      if (page <= 2) {
        // Back to the top — drop the cursor so we get the true first page
        // rather than "the 25 above wherever we happen to be".
        setCursor(null);
        setPage(1);
      } else {
        setCursor({ dir: "prev", orderNum: results.firstOrderNum });
        setPage(page - 1);
      }
    }
  };

  // Client-side per-column filtering of the loaded page (no API call). Each
  // column's text does a case-insensitive substring match against its value.
  const cnorm = (s) => String(s ?? "").toLowerCase();
  const visibleOrders = (() => {
    const f = colFilters;
    if (!Object.values(f).some((v) => v.trim() !== "")) return results.orders;
    return results.orders.filter((o) => {
      const st = orderStatusText(o);
      return (!f.orderNum  || cnorm(o.orderNum).includes(cnorm(f.orderNum)))
          && (!f.customer  || cnorm(`${o.customerName} ${o.customerCustID}`).includes(cnorm(f.customer)))
          && (!f.poNum     || cnorm(o.poNum).includes(cnorm(f.poNum)))
          && (!f.salesRep  || cnorm(o.salesRepList).includes(cnorm(f.salesRep)))
          && (!f.orderDate || cnorm(fmtDate(o.orderDate)).includes(cnorm(f.orderDate)))
          && (!f.total     || cnorm(fmtMoney(o.docOrderAmt)).includes(cnorm(f.total)) || cnorm(o.docOrderAmt).includes(cnorm(f.total)))
          && (!f.status    || cnorm(st).includes(cnorm(f.status)));
    });
  })();

  // ── Render ──────────────────────────────────────────────────────────────
  return (
    <div style={{ display: "flow-root" }}>
    <div className="qw-root content-halo dyn-qw dyn-search-shell">
      {/* Ribbon header — icon left, centered title, Quotes shortcut right */}
      {/* Slimmed from the default ribbon padding: this banner is decoration and
          was costing ~95px on a page whose whole complaint is grid height. */}
      <div className="qw-header qw-header-centered" style={{ marginBottom: 10, padding: "10px 20px" }}>
        <div className="qw-header-icon">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M6 2h9l5 5v13a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2z" />
            <polyline points="14 2 14 8 20 8" />
            <line x1="8" y1="13" x2="16" y2="13" />
            <line x1="8" y1="17" x2="16" y2="17" />
            <line x1="8" y1="9" x2="10" y2="9" />
          </svg>
        </div>
        <div className="qw-header-title">Orders</div>
      </div>

      {/* ── Filters panel (mobile only) ──────────────────────────────────── */}
      {/* On desktop every filter lives in the grid column headers below; the
          collapsible panel renders only on phones, where the card list has no
          column headers. */}
      <div>
      <CollapsibleFilters collapsed={filtersCollapsed} onCollapsedChange={setFiltersCollapsed}
        summary={activeFilterSummary}>
      <div className="dyn-qw-panel" style={{ padding: "10px 14px", marginBottom: 10 }}>
        <div className="dyn-qw-filters dyn-qw-filters-q12 dyn-qw-filters-q14">
          {/* Order # — exact match (press Enter or Search). */}
          <div className="dyn-qw-filter-group qw-col-2">
            <label className="dyn-qw-filter-label">Order #</label>
            <Form.Control
              size="sm"
              placeholder="Order #…"
              value={orderNumFind}
              onChange={(e) => setOrderNumFind(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") handleSearchClick(); }}
            />
          </div>
          <div className="dyn-qw-filter-group qw-col-2">
            <label className="dyn-qw-filter-label">Territory</label>
            <SearchableSelect
              inputWidth="100%"
              config={AC_TERRITORY}
              value={filters.territoryID}
              onChange={(v) => setFilters((f) => ({ ...f, territoryID: v }))}
              onSelectItem={(item) => {
                const t = item.territoryID || item.TerritoryID || "";
                setFilters((f) => {
                  const next = { ...f, territoryID: t };
                  if (f.salesRepCode && !f.salesRepCode.startsWith(`${t}-`) && f.salesRepCode !== t) {
                    next.salesRepCode = "";
                  }
                  if (f.custID) next.custID = "";
                  return next;
                });
              }}
              placeholder="ID or description…"
            />
          </div>
          <div className="dyn-qw-filter-group qw-col-2">
            <label className="dyn-qw-filter-label">Sales Rep</label>
            <SearchableSelect
              inputWidth="100%"
              config={salesRepConfig}
              value={filters.salesRepCode}
              onChange={(v) => setFilters((f) => ({ ...f, salesRepCode: v }))}
              onSelectItem={(item) => {
                const code = item.salesRepCode || item.SalesRepCode || "";
                setFilters((f) => {
                  const next = { ...f, salesRepCode: code };
                  if (!f.territoryID && code.includes("-")) {
                    next.territoryID = code.split("-")[0];
                  }
                  if (f.custID) next.custID = "";
                  return next;
                });
              }}
              placeholder={filters.territoryID ? `Reps in ${filters.territoryID}…` : "Code or name…"}
            />
          </div>
          <div className="dyn-qw-filter-group qw-col-2">
            <label className="dyn-qw-filter-label">Customer</label>
            <SearchableSelect
              config={customerConfig}
              value={filters.custID}
              onChange={(v) => setFilters((f) => ({ ...f, custID: v }))}
              onSelectItem={(item) => {
                const id = item.custID || item.CustID || "";
                const repFromCust = item.salesRepCode || item.SalesRepCode || "";
                const terrFromCust = item.territoryID || item.TerritoryID || "";
                setFilters((f) => {
                  const next = { ...f, custID: id };
                  if (repFromCust && !f.salesRepCode) next.salesRepCode = repFromCust;
                  if (terrFromCust && !f.territoryID) next.territoryID = terrFromCust;
                  else if (!terrFromCust && repFromCust && !f.territoryID && repFromCust.includes("-")) {
                    next.territoryID = repFromCust.split("-")[0];
                  }
                  return next;
                });
              }}
              placeholder={
                filters.salesRepCode ? `Customers of ${filters.salesRepCode}…`
                  : filters.territoryID ? `Customers in ${filters.territoryID}…`
                  : "ID or name…"
              }
              inputWidth="100%"
            />
          </div>
          <div className="dyn-qw-filter-group qw-col-2">
            <label className="dyn-qw-filter-label">PO #</label>
            <Form.Control
              size="sm"
              placeholder="Customer PO…"
              value={filters.poNum}
              onChange={(e) => setFilters((f) => ({ ...f, poNum: e.target.value }))}
              onKeyDown={(e) => { if (e.key === "Enter") handleSearchClick(); }}
            />
          </div>
          <div className="dyn-qw-filter-group qw-col-2">
            <label className="dyn-qw-filter-label">Status</label>
            <Form.Select size="sm" value={filters.status} onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))}>
              {STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </Form.Select>
          </div>
          {/* Date pickers removed. They cost a whole filter row — and with the
              grid squeezed to ~2 visible rows, that row mattered more than the
              control did. Omitting startDate/endDate makes the server apply its
              own defaults (reps 3 months back, admins 15 days — see
              ListSalesOrdersHandler), which is what the app used anyway. */}
          <div className="dyn-qw-filter-group dyn-qw-actions-cell">
            <div className="d-flex gap-2" style={{ justifyContent: "flex-end", flexWrap: "wrap" }}>
              <Button type="button" variant="outline-secondary" size="sm" onClick={handleClearFilters} disabled={loading}>
                Clear
              </Button>
              <Button type="button" variant="primary" size="sm" onClick={handleSearchClick} disabled={loading} style={{ fontWeight: 600 }}>
                {loading
                  ? <><Spinner animation="border" size="sm" /> Searching…</>
                  : <><FaSearch style={{ marginRight: 6 }} /> Search</>}
              </Button>
            </div>
          </div>
        </div>
      </div>
      </CollapsibleFilters>
      </div>

      {/* ── Results panel — scrolls internally so the page stays put ───────── */}
      <div className="dyn-results-wrap">
      <div ref={resultsScrollRef} className="dyn-results-scroll" onScroll={onResultsScroll}>
      <div className="dyn-qw-panel" style={{ padding: "10px 14px" }}>
          {error && <Alert variant="danger">{error}</Alert>}
          {/* Live mode's scan ceiling tripped before the page filled: say so, or an empty
              page with Next still on reads like a dead end (UI-ORD-025). */}
          {!error && !loading && results.scanCapHit && (
            <Alert variant="warning" style={{ fontSize: 12.5, padding: "6px 10px" }}>
              {results.message || "Partial results — scan limit reached."} Next keeps searching older orders.
            </Alert>
          )}

              {/* Desktop grid (≥992px) */}
              <div className="d-none d-lg-block" style={{ border: "1px solid #E2E8F0", borderRadius: 12, background: "#fff" }}>
              <table className="dyn-table-dense" style={{ width: "100%", tableLayout: "fixed", borderCollapse: "collapse", fontSize: 13 }}>
                {/* Fixed proportional columns. Keep each col alone on its line
                    with no trailing inline comment (a JSX comment leaves a
                    whitespace text node, invalid inside colgroup). Widths:
                    Order# 10, Customer 22, PO# 12, Sales Rep 14, Order date 13,
                    Total 12, Status 17. */}
                <colgroup>
                  <col style={{ width: "4%" }} />
                  <col style={{ width: "10%" }} />
                  <col style={{ width: "20%" }} />
                  <col style={{ width: "11%" }} />
                  <col style={{ width: "13%" }} />
                  <col style={{ width: "13%" }} />
                  <col style={{ width: "12%" }} />
                  <col style={{ width: "17%" }} />
                </colgroup>
                <thead>
                  <tr style={{ background: "#F8FAFC", textAlign: "left", color: "#64748B", fontSize: 11, textTransform: "uppercase", letterSpacing: ".04em" }}>
                    <th style={QTH} aria-label="Details" />
                    <th style={QTH}>Order #</th>
                    <th style={QTH}>Customer</th>
                    <th style={QTH}>PO #</th>
                    <th style={QTH}>Sales Rep</th>
                    <th style={QTH}>Order Date</th>
                    <th style={QTH}>Total</th>
                    <th style={QTH}>Status</th>
                  </tr>
                  {/* Per-column CLIENT-SIDE search — instantly narrows the rows
                      already loaded (the panel above queries the server). Kept
                      visible; the row is just slimmer than it was. */}
                  {showColFilters && (
                  <tr className="dyn-colfilter">
                    <th style={QTHF} />
                    <th style={QTHF}><Form.Control size="sm" placeholder="Filter…" value={colFilters.orderNum} onChange={(e) => setCol("orderNum", e.target.value)} /></th>
                    <th style={QTHF}><Form.Control size="sm" placeholder="Filter…" value={colFilters.customer} onChange={(e) => setCol("customer", e.target.value)} /></th>
                    <th style={QTHF}><Form.Control size="sm" placeholder="Filter…" value={colFilters.poNum} onChange={(e) => setCol("poNum", e.target.value)} /></th>
                    <th style={QTHF}><Form.Control size="sm" placeholder="Filter…" value={colFilters.salesRep} onChange={(e) => setCol("salesRep", e.target.value)} /></th>
                    <th style={QTHF}><Form.Control size="sm" placeholder="Filter…" value={colFilters.orderDate} onChange={(e) => setCol("orderDate", e.target.value)} /></th>
                    <th style={QTHF}><Form.Control size="sm" placeholder="Filter…" value={colFilters.total} onChange={(e) => setCol("total", e.target.value)} /></th>
                    <th style={QTHF}><Form.Control size="sm" placeholder="Filter…" value={colFilters.status} onChange={(e) => setCol("status", e.target.value)} /></th>
                  </tr>
                  )}
                </thead>
                <tbody>
                  {visibleOrders.map((o) => (
                    <React.Fragment key={o.orderNum}>
                    <tr
                      style={{ cursor: "pointer" }}
                      onClick={() => navigate(`/order/${o.orderNum}`)}
                      title="Click to view order"
                    >
                      {/* Details toggle — expands the order → shipments flow
                          under the row. Only rendered when the order has packs
                          (nothing to expand otherwise). stopPropagation so it
                          doesn't trigger the row's click-to-view. */}
                      <td onClick={(e) => e.stopPropagation()} style={{ cursor: "default" }}>
                        {orderHasShipments(o) ? (
                          <button
                            type="button"
                            className={"qflow-toggle" + (expandedOrders.has(o.orderNum) ? " open" : "")}
                            title={expandedOrders.has(o.orderNum) ? "Hide details" : "Show shipment details"}
                            aria-expanded={expandedOrders.has(o.orderNum)}
                            onClick={() => toggleExpanded(o.orderNum)}
                          >
                            <ChevronRight size={16} />
                          </button>
                        ) : null}
                      </td>
                      <td className="fw-semibold" style={{ color: "#0052A5" }}>#{o.orderNum}</td>
                      <td>
                        <div>{o.customerName || "—"}</div>
                        {o.customerCustID && <div className="dyn-cell-sub">{o.customerCustID}</div>}
                      </td>
                      <td>{o.poNum || "—"}</td>
                      <td>{o.salesRepList || "—"}</td>
                      <td>{fmtDate(o.orderDate)}</td>
                      <td>{fmtMoney(o.docOrderAmt)}</td>
                      <td>
                        {/* Badge and fulfillment note side by side rather than
                            stacked — both are short, and the stacked pair was
                            the tallest thing in the row. */}
                        <div className="dyn-cell-inline">
                          <OrderStatusBadge o={o} />
                          <FulfillmentText o={o} />
                        </div>
                      </td>
                    </tr>
                    {expandedOrders.has(o.orderNum) && (
                      <tr>
                        {/* Empty cell under the chevron column so the chips
                            start aligned under the Order # column. */}
                        <td className="qflow-cell" />
                        <td colSpan={7} className="qflow-cell">
                          <OrderShipFlow o={o} navigate={navigate} />
                        </td>
                      </tr>
                    )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
              {loading && (
                <div style={{ padding: 24, textAlign: "center", color: "#666" }}>
                  <Spinner animation="border" size="sm" /> Loading orders…
                </div>
              )}
              {!loading && results.orders.length === 0 && (
                <div style={{ padding: 24, textAlign: "center", color: "#94A3B8" }}>
                  {/* A failed load says so inside the grid too, not only in the
                      red alert above (SAL-GAP-86, Kapil 2026-10-23). */}
                  {error
                    ? <>Orders could not be loaded - try again. <Button variant="link" size="sm" style={{ padding: 0, verticalAlign: "baseline" }} onClick={() => runSearch()}>Try again</Button></>
                    : warmGaveUp
                    ? <>Orders are still loading on the server. <Button variant="link" size="sm" style={{ padding: 0, verticalAlign: "baseline" }} onClick={retryWarm}>Retry</Button></>
                    : warming
                    ? <><Spinner animation="border" size="sm" /> Loading orders from Epicor — this can take a few minutes after a restart. Retrying automatically…</>
                    : hasSearched ? "No orders match the current filters." : "Set filters and hit Search."}
                </div>
              )}
              {!loading && results.orders.length > 0 && visibleOrders.length === 0 && (
                <div style={{ padding: 24, textAlign: "center", color: "#94A3B8" }}>
                  No rows on this page match the column filters.
                </div>
              )}
              </div>

              {/* Mobile cards (<992px) — one card per order, tap to view */}
              <div className="d-lg-none">
                {!filtersCollapsed ? (
                  <Alert variant="light" className="text-center mb-0" style={{ fontSize: 13 }}>
                    Set filters above, then tap <strong>Search</strong> to see results.
                  </Alert>
                ) : (<>
                {loading && (
                  <div style={{ padding: 30, textAlign: "center", color: "#666" }}>
                    <Spinner animation="border" size="sm" /> Loading orders…
                  </div>
                )}
                {!loading && !hasSearched && !error && (
                  <Alert variant="light">Set any filters above and hit <strong>Search</strong> to see orders.</Alert>
                )}
                {!loading && error && results.orders.length === 0 && (
                  <Alert variant="light" className="text-center" style={{ fontSize: 13 }}>
                    Orders could not be loaded - try again.{" "}
                    <Button variant="link" size="sm" style={{ padding: 0, verticalAlign: "baseline" }} onClick={() => runSearch()}>Try again</Button>
                  </Alert>
                )}
                {!loading && hasSearched && results.orders.length === 0 && !error && (
                  warmGaveUp
                    ? <Alert variant="light" className="text-center" style={{ fontSize: 13 }}>
                        Orders are still loading on the server.{" "}
                        <Button variant="link" size="sm" style={{ padding: 0, verticalAlign: "baseline" }} onClick={retryWarm}>Retry</Button>
                      </Alert>
                    : warming
                    ? <Alert variant="light" className="text-center" style={{ fontSize: 13 }}>
                        <Spinner animation="border" size="sm" /> Loading orders from Epicor — this can take a few
                        minutes after a restart. Retrying automatically…
                      </Alert>
                    : <Alert variant="light">No orders match the current filters.</Alert>
                )}
                {!loading && results.orders.map((o) => (
                  <div
                    key={o.orderNum}
                    onClick={() => navigate(`/order/${o.orderNum}`)}
                    className="qw-quote-card"
                  >
                    {/* Row 1: details toggle · order # · customer (+ id) · status.
                        Toggle only when the order has packs; a hidden placeholder
                        keeps the order #s aligned across cards. */}
                    <div className="qc-row">
                      {orderHasShipments(o) ? (
                        <button
                          type="button"
                          className={"qflow-toggle qflow-toggle-sm" + (expandedOrders.has(o.orderNum) ? " open" : "")}
                          title={expandedOrders.has(o.orderNum) ? "Hide details" : "Show shipment details"}
                          aria-expanded={expandedOrders.has(o.orderNum)}
                          onClick={(e) => { e.stopPropagation(); toggleExpanded(o.orderNum); }}
                        >
                          <ChevronRight size={13} />
                        </button>
                      ) : (
                        <span className="qflow-toggle qflow-toggle-sm" style={{ visibility: "hidden" }} aria-hidden="true" />
                      )}
                      <span className="qc-num">#{o.orderNum}</span>
                      <span className="qc-cust" title={o.customerName || ""}>
                        {o.customerName || "—"}
                        {o.customerCustID ? <span className="qc-custid"> · {o.customerCustID}</span> : null}
                      </span>
                      <OrderStatusBadge o={o} />
                    </div>
                    {/* Row 2: total · rep · PO · date */}
                    <div className="qc-row">
                      <span className="qc-total">{fmtMoney(o.docOrderAmt)}</span>
                      <span className="qc-meta" title={o.salesRepList || ""}>
                        {o.salesRepList || "—"}
                        {o.poNum ? ` · PO ${o.poNum}` : ""}
                      </span>
                      <span className="qc-dates">{fmtDateShort(o.orderDate)}</span>
                    </div>
                    {/* Row 3: shipped/invoiced rollups. Renders nothing when
                        there is nothing to report, so most cards stay two
                        rows. Mirrors the desktop table's status column, which
                        is where this used to live only — leaving phone and
                        tablet users with no fulfillment information at all. */}
                    <FulfillmentText o={o} />
                    {expandedOrders.has(o.orderNum) && (
                      <div className="qflow-mobile" onClick={(e) => e.stopPropagation()}>
                        <OrderShipFlow o={o} navigate={navigate} />
                      </div>
                    )}
                  </div>
                ))}
                </>)}
              </div>

      </div>
      </div>
      {moreBelow && <div className="dyn-results-fade" aria-hidden="true" />}
      </div>
      {/* Pinned pagination footer (outside the scroll region). */}
      {hasSearched && (
        <GridPagination
          currentPage={page - 1}
          // Always the simple Prev/Next pager (hasNext defined ⇒ no numbered
          // pages). Live mode: the server states whether more rows exist.
          // Cached mode has a real total — compare against the page count so
          // Next stays alive past page 1 (hasMore is undefined there).
          hasNext={results.hasMore !== undefined
            ? results.hasMore === true
            : page < (results.totalPages || 0)}
          totalPages={results.totalPages || 0}
          totalCount={results.hasMore !== undefined
            ? null
            : (results.totalCount ?? results.orders.length)}
          // An empty page reads "Showing 0–0", never "Showing 0–25" (UI-ORD-025).
          showFrom={results.orders.length ? (page - 1) * pageSize + 1 : 0}
          showTo={results.orders.length ? (page - 1) * pageSize + results.orders.length : 0}
          onGoToPage={goToPage}
          extra={
            <div className="d-flex align-items-center" style={{ gap: 10 }}>
              <button
                type="button"
                onClick={() => setShowColFilters((v) => {
                  // Clearing on hide matters: a stale column filter would keep
                  // narrowing the grid with nothing on screen explaining why.
                  if (v) setColFilters({ orderNum: "", customer: "", poNum: "", salesRep: "", orderDate: "", total: "", status: "" });
                  return !v;
                })}
                title={showColFilters
                  ? "Hide the in-grid column search (frees a row of height)"
                  : "Show the in-grid column search — filters the rows on this page"}
                style={{
                  border: "1px solid #E2E8F0", background: showColFilters ? "#EDE9FE" : "#fff",
                  color: showColFilters ? "#5B21B6" : "#64748B", borderRadius: 8,
                  fontSize: 12, fontWeight: 600, padding: "4px 10px", cursor: "pointer",
                }}
              >
                <FaSearch style={{ marginRight: 5, fontSize: 10 }} />
                Column filters
              </button>
              <span style={{ fontSize: 12, color: "#64748B" }}>Rows</span>
              <Form.Select size="sm" value={pageSize} onChange={(e) => { clearPageCache(); setCursor(null); setPage(1); setPageSize(Number(e.target.value)); }} style={{ width: "auto", minWidth: 64 }} aria-label="Page size">
                {PAGE_SIZES.map((n) => <option key={n} value={n}>{n}</option>)}
              </Form.Select>
            </div>
          }
        />
      )}
    </div>
    </div>
  );
}

// =============================================================================
// OrderStatusBadge — the order's own state (Held > Closed > Open), with the
// customer's credit hold as a second badge beside it, never in its place
// (UI-ORD-016: a Closed order on a held customer read only "Credit Hold").
// (The list DTO has no Voided flag, so voided isn't shown here.)
// =============================================================================
// Plain-text status (mirrors OrderStatusBadge) — used by the column search.
function orderStatusText(o) {
  const own = o.orderHeld ? "Held" : !o.openOrder ? "Closed" : "Open";
  return o.custOnCreditHold ? `${own} Credit Hold` : own;
}

// Does this order have any packs to show in the expand flow? Prefer the
// server's shipment-cache flag (counts staged packs too); while that cache is
// still cold the flag is null and we fall back to the shipped/invoiced
// rollups. Drives whether the row renders an expand chevron at all.
function orderHasShipments(o) {
  if (o.hasShipments === true || o.hasShipments === false) return o.hasShipments;
  return Boolean(
    (o.shippedStatus && o.shippedStatus !== "none") ||
    (o.invoicedStatus && o.invoicedStatus !== "none")
  );
}

function OrderStatusBadge({ o }) {
  const own = o.orderHeld
    ? <Badge bg="warning" text="dark">Held</Badge>
    : !o.openOrder
      ? <Badge bg="secondary">Closed</Badge>
      : <Badge bg="primary">Open</Badge>;
  if (!o.custOnCreditHold) return own;
  return <>{own} <Badge bg="danger">Credit Hold</Badge></>;
}

// Small secondary line under the status badge summarising fulfillment — the
// shipped/invoiced rollups the API computes ("none" | "partial" | "full").
function FulfillmentText({ o }) {
  const bits = [];
  if (o.shippedStatus && o.shippedStatus !== "none")   bits.push(`Shipped: ${o.shippedStatus}`);
  if (o.invoicedStatus && o.invoicedStatus !== "none") bits.push(`Invoiced: ${o.invoicedStatus}`);
  if (!bits.length) return null;
  return <span className="dyn-cell-sub" style={{ marginTop: 0 }}>{bits.join(" · ")}</span>;
}

// =============================================================================
// OrderShipFlow — the expanded "details" row: a left-to-right linkage flow
// chart Order → Shipments (every pack cut against the order). Mirrors the
// Quote page's QuoteFlow and shares its .qflow-* styles (DynamicQw.css).
// Loads lazily on first expand (one /api/shipments?orderNum= lookup) and keeps
// results in a module-level cache so re-expanding is instant; the fetch still
// re-fires each expand to quietly refresh the cache (shipment status changes
// as the warehouse works).
// =============================================================================
const shipFlowCache = sessionMap(); // orderNum -> shipments[] (emptied on sign-out / new sign-in)

// Shipment status text/badge mirror the Shipping page's grid.
function shipFlowStatusText(s) {
  if (s.voided) return "Voided";
  const st = (s.shipStatus || "").toLowerCase();
  if (st.includes("invoic")) return "Invoiced";
  if (st.includes("ship"))   return "Shipped";
  if (st.includes("stage"))  return "Staged";
  if (st.includes("close"))  return "Closed";
  if (st.includes("open") || !s.shipStatus) return "Open";
  return s.shipStatus;
}
function ShipFlowBadge({ s }) {
  const text = shipFlowStatusText(s);
  const bg =
    text === "Voided" ? "danger"
    : text === "Invoiced" || text === "Shipped" ? "success"
    : text === "Staged" ? "warning"
    : text === "Closed" ? "secondary"
    : "primary";
  return <Badge bg={bg} text={bg === "warning" ? "dark" : undefined}>{text}</Badge>;
}

function FlowArrow() {
  return (
    <div className="qflow-link" aria-hidden="true">
      <span className="qflow-line" />
      <span className="qflow-head" />
    </div>
  );
}

function OrderShipFlow({ o, navigate }) {
  const orderNum = o.orderNum;
  const [state, setState] = useState(() => ({
    loading: !shipFlowCache.has(orderNum),
    error: null,
    shipments: shipFlowCache.get(orderNum) || null,
  }));

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(
          `${API_URL}/api/shipments?orderNum=${orderNum}&page=1&pageSize=100&sortBy=PackNum&sortDir=asc`,
          { headers: authHeaders() }
        );
        const data = await r.json();
        if (cancelled) return;
        const shipments = Array.isArray(data?.shipments) ? data.shipments : [];
        shipFlowCache.set(orderNum, shipments);
        setState({ loading: false, error: null, shipments });
      } catch (e) {
        if (cancelled) return;
        // A failed refresh over cached data keeps the cached flow on screen.
        setState((s) => (s.shipments ? { ...s, loading: false } : { loading: false, error: String(e), shipments: null }));
      }
    })();
    return () => { cancelled = true; };
  }, [orderNum]);

  const { loading, error, shipments } = state;

  // Compact one-line chips. The row above already IS the order, so the panel
  // shows only what hangs off it: its packs.
  return (
    <div className="qflow" onClick={(e) => e.stopPropagation()}>
      <span className="qflow-stage-label">Shipments</span>
      <FlowArrow />
      {!shipments || shipments.length === 0 ? (
        <span className="qflow-chip qflow-ghost">
          {loading ? <><Spinner animation="border" size="sm" /> Loading shipments…</>
            : error ? "Couldn't load shipments"
            : "No shipments yet"}
        </span>
      ) : (
        shipments.map((s) => (
          /* Just identity + status — full detail lives on the shipment view
             page the chip opens. */
          <span
            key={s.packNum}
            className="qflow-chip qflow-chip-ship"
            role="button"
            title="Open shipment details"
            onClick={() => navigate(`/shipment/${s.packNum}`)}
          >
            <span className="qflow-chip-label">Pack</span>
            <span className="qflow-chip-id">#{s.packNum}</span>
            <ShipFlowBadge s={s} />
          </span>
        ))
      )}
    </div>
  );
}

export default Order;
